import * as React from 'react';
import { Filter } from 'lucide-react';
import { Button } from './Button';
import { DiffLine } from './DiffLine';
import { Icon } from './Icon';
import { Divider, Inline, Stack } from './Layout';
import { SectionCard } from './SectionCard';
import { Loading } from './Spinner';
import { Text } from './Text';
import { ToggleGroup, ToggleGroupItem } from './ToggleGroup';

export type ActivityLogGroup = 'driver' | 'dispatch' | 'system';
export type ActivityLogFilter = 'all' | 'driver' | 'dispatch';

export interface ActivityLogChange {
  kind: 'add' | 'remove' | 'change';
  /** The field's name; absent for a one-phrase line such as "2 Images". */
  field?: string;
  /** For a change: what it was. */
  from?: string;
  value: string;
}

export interface ActivityLogEntry {
  id: string;
  /** "Dispatch Update", "Vehicle Update"… */
  action: string;
  /** Who made it, as the log names them. Omit when `action` already says. */
  actor?: string;
  /** Already formatted. */
  date: string;
  time: string;
  /** Which filter it belongs to. */
  group: ActivityLogGroup;
  changes: ActivityLogChange[];
  /** What can be done about the event, drawn at the row's end. */
  actions?: React.ReactNode;
}

export interface ActivityLogViewProps {
  /** Every event with something to show, newest first. */
  entries: ActivityLogEntry[];
  /** Draw only the first so many; omit to draw them all. */
  limit?: number;
  /** The record may hold more than was fetched: the count carries a plus. */
  moreAvailable?: boolean;
  /** Offered when `moreAvailable`. */
  onShowAll?: () => void;
  /** Offered when everything is drawn and there is more than a page of it. */
  onCollapse?: () => void;
  loading?: boolean;
}

/**
 * The entries a filter keeps: all of them, or only a driver's or only
 * dispatch's. The desk's log and the phone's (ActivityLogList) both filter
 * by it, so the two cannot disagree about what "Driver" means.
 */
export const activityInFilter = <T extends ActivityLogEntry>(
  entries: T[],
  filter: ActivityLogFilter
): T[] =>
  filter === 'all'
    ? entries
    : entries.filter((entry) => entry.group === filter);

const FILTERS: { key: ActivityLogFilter; label: string }[] = [
  { key: 'all', label: 'All Activity' },
  { key: 'driver', label: 'Driver' },
  { key: 'dispatch', label: 'Dispatch' },
];

/**
 * The call activity log: an audit trail of what changed on the vehicle, one
 * block per event with its actor and timestamp and a diff line per change,
 * filterable to driver or dispatch activity, and paged between the latest
 * five and everything. An event with no changes, such as a share recipient
 * being given access, is one line: an action that names them, and the
 * time; one with `actions` carries them at the row's end.
 *
 *   <ActivityLogView entries={events} limit={expanded ? undefined : 5}
 *     moreAvailable={!expanded && fetched.length > 5}
 *     onShowAll={() => setExpanded(true)} onCollapse={() => setExpanded(false)} />
 *
 * The filter is the log's own state; the counts on the filter buttons are
 * over every entry, not just the ones drawn.
 */
export function ActivityLogView({
  entries,
  limit,
  moreAvailable = false,
  onShowAll,
  onCollapse,
  loading = false,
}: ActivityLogViewProps) {
  const [filter, setFilter] = React.useState<ActivityLogFilter>('all');

  const shown = limit === undefined ? entries : entries.slice(0, limit);
  const filtered = activityInFilter(shown, filter);
  const count = (key: ActivityLogFilter) =>
    activityInFilter(entries, key).length;
  const expanded = limit === undefined;

  return (
    <SectionCard
      title={
        <Stack gap={0}>
          <Text variant="h4">Call Activity Log</Text>
          <Text variant="caption" tone="muted">
            Complete audit trail · {entries.length} events
            {moreAvailable ? '+' : ''}
          </Text>
        </Stack>
      }
      actions={
        <>
          <Icon
            icon={Filter}
            size="xs"
            tone="muted"
            className="hidden sm:block"
          />
          <ToggleGroup
            type="single"
            size="sm"
            value={filter}
            // Radix clears the value when the on item is pressed again; one
            // filter is always in effect, so ignore that.
            onValueChange={(value) => {
              if (value) setFilter(value as ActivityLogFilter);
            }}
            aria-label="Filter activity"
          >
            {FILTERS.map((f) => (
              <ToggleGroupItem key={f.key} value={f.key}>
                {f.label}
                <Text as="span" variant="inherit" className="opacity-70">
                  {count(f.key)}
                </Text>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </>
      }
    >
      <>
        {loading ? (
          <Loading size="lg" pad="md" />
        ) : filtered.length === 0 ? (
          <Text variant="body-sm" tone="muted" align="center">
            {entries.length === 0
              ? 'No history found for this vehicle.'
              : 'No events match this filter.'}
          </Text>
        ) : (
          <Stack gap={4}>
            {filtered.map((entry, index) => (
              <React.Fragment key={entry.id}>
                {index > 0 && <Divider />}
                <Stack gap={2}>
                  <Inline gap={3} align="center" justify="between" wrap>
                    <Text variant="body-sm-strong">{entry.action}</Text>
                    <Inline gap={2} align="baseline" className="shrink-0">
                      <Text as="span" variant="caption" tone="muted" numeric>
                        {entry.date}
                      </Text>
                      <Text as="span" variant="caption-strong" numeric>
                        {entry.time}
                      </Text>
                      {entry.actions}
                    </Inline>
                  </Inline>

                  {entry.actor ? (
                    <Text as="span" variant="caption-strong">
                      {entry.actor}
                    </Text>
                  ) : null}

                  {entry.changes.length > 0 && (
                    <Stack gap={1}>
                      {entry.changes.map((change, i) => (
                        <DiffLine
                          key={`${change.field ?? ''}:${change.value}:${i}`}
                          kind={change.kind}
                          field={change.field}
                          from={change.from}
                          value={change.value}
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              </React.Fragment>
            ))}

            {moreAvailable && onShowAll && (
              <Button variant="outline" size="sm" fullWidth onClick={onShowAll}>
                View All History
              </Button>
            )}
            {expanded && entries.length > 5 && onCollapse && (
              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={onCollapse}
              >
                Show Latest 5
              </Button>
            )}
          </Stack>
        )}
      </>
    </SectionCard>
  );
}
